import { rem } from 'polished'
import React, {
  MouseEventHandler,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import styled from 'styled-components'

import SavedItemRemoveButton from '../ItemCard/Common/SavedItemRemoveButton'
import { SavedBottomContent } from '../ItemCard/SavedBottomContent'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import { ItemCardVariant } from '../ItemCard/types'
import { AddRemoveItineraryButton } from '../Itinerary/AddRemoveItineraryButton'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import HeadingFullPrint from '../util/HeadingFullPrint'

import NumberRating from 'components/Common/NumberRating/NumberRating'
import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import LinePaperclipIcon from 'components/Luxkit/Icons/line/LinePaperclipIcon'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import NoPrint from 'components/utils/NoPrint'
import PrintOnly from 'components/utils/PrintOnly'
import { verticalChildSpacing } from 'components/utils/spacing'
import { useAppDispatch } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { memoWithNestedProps } from 'lib/memoize/memoWithNestedProps'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import { useIsTabletAndSmallerScreen } from 'lib/web/deviceUtils'
import zIndex from 'styles/tools/z-index'
import AttachedDocument from 'tripPlanner/components/Documents/AttachedDocument'
import { DesktopLayoutSizes } from 'tripPlanner/components/ItemCard/Common/utils'
import { ItemImageContainer } from 'tripPlanner/components/ItemCard/ItemImageContainer'
import BaseSavedOffer from 'tripPlanner/components/SavedOffers/BaseSavedOffer'
import TripContext from 'tripPlanner/contexts/TripContext'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { ItemMetadata } from 'tripPlanner/models'
import { setCurrentSectionId } from 'tripPlanner/reducers/actions'
import { TripItem } from 'tripPlanner/types/tripItem'
import { getItemPlace } from 'tripPlanner/utils'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const Content = styled.div`
  position: relative;
  flex: 1;
  padding: ${rem(20)};
  border: 1px dashed ${props => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.M};

  @media print {
    border-top: 1px dashed ${props => props.theme.palette.neutral.default.five};
  }

  @media not print {
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    ${DesktopLayoutSizes} {
      border-top: 1px dashed
        ${props => props.theme.palette.neutral.default.five};
      border-left: 0;
      border-top-right-radius: ${(props) => props.theme.borderRadius.M};
      border-bottom-left-radius: 0;
    }
  }
`

const TopRowGroup = styled(Group)`
  ${DesktopLayoutSizes} {
    min-height: ${rem(20)};
  }
`

const DesktopLayoutNumberRating = styled(NumberRating)`
  display: none;

  ${DesktopLayoutSizes} {
    display: flex;
  }
`

const NonDesktopLayoutGroup = styled(Group)`
  ${DesktopLayoutSizes} {
    display: none;
  }
`

const SavedButtonContainer = styled.div`
  position: absolute;
  right: ${rem(16)};
  top: 0;
  visibility: hidden;

  ${DesktopLayoutSizes} {
    visibility: unset;
    top: ${rem(16)};
    transform: unset;
    z-index: ${zIndex.floatingBookmarkButton};
  }
`

const MainContent = styled.div`
  flex: 1;
  ${verticalChildSpacing[8]}
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
`

interface Props {
  item: TripItem
  name: string
  propertyDescription?: string
  imgId?: string
  imgSrc?: string
  placeId?: string
  descriptors?: Array<string | undefined>
  /** Alternate `descriptors` array to use for print view */
  printDescriptors?: Array<string | undefined>
  locationDescription?: React.ReactNode
  imageOverlayContent?: React.ReactNode
  priceAreaContent?: React.ReactNode
  ctaAreaContent?: React.ReactNode
  urgencyLabels?: React.ReactNode
  to?: string
  isSoldOut?: boolean
  testId?: string
  /** Disables click handler, link and menu */
  nonInteractable?: boolean
  offerLink: string
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
  inclusions?: React.ReactNode
  amenitiesContent?: React.ReactNode
  rating?: number
  numberOfReviews?: number
}

function SavedOfferCard({
  item,
  name,
  propertyDescription,
  imageOverlayContent,
  imgId,
  imgSrc,
  locationDescription,
  placeId,
  priceAreaContent,
  ctaAreaContent,
  urgencyLabels,
  to,
  isSoldOut,
  descriptors = EmptyArray,
  printDescriptors = EmptyArray,
  testId,
  nonInteractable = false,
  offerLink,
  tripNameLabel,
  variant,
  inclusions,
  amenitiesContent,
  rating,
  numberOfReviews,
}: PropsWithChildren<Props>) {
  const { type } = item
  const Icon = ItemMetadata[type].icon

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const descriptorsString = nonNullable(descriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  const printDescriptorsString = nonNullable(printDescriptors)
    .filter((text) => text.length > 0)
    .join(' · ')
  // If we have different descriptors for print view, only show the normal ones in non-print view
  const DescriptorsWrapper = printDescriptorsString ? NoPrint : React.Fragment

  const dispatch = useAppDispatch()

  const canShowOnMap = useMemo(() => {
    const place = getItemPlace(item, 'start')
    return place?.type == 'GEO' && !!place.point
  }, [item])

  const showOnMap = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault()
      if (canShowOnMap) {
        dispatch(setCurrentSectionId(itemSectionId(item.id)))
      }
    },
    [canShowOnMap, dispatch, item.id],
  )

  const navigateTo = useNavigateTo()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const onClickAddTripItemDates = useCallback(() => {
    navigateTo(offerLink + '#date-picker')
    setImmersiveMode({ tripId: item.tripId, sectionId: itemSectionId(item.id) })
  }, [navigateTo, offerLink, setImmersiveMode, item.tripId, item.id])

  const saveButtonAndItineraryButtonGroup = useMemo(
    () => (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {variant === 'tripSummary' && (
          <AddRemoveItineraryButton
            item={item}
            onClickAddTripItemDates={onClickAddTripItemDates}
          />
        )}
        <SavedItemRemoveButton item={item} />
      </Group>
    ),
    [item, onClickAddTripItemDates, variant],
  )

  const isTabletOrSmaller = useIsTabletAndSmallerScreen()

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  return (
    <BaseSavedOffer
      to={to}
      tripId={item.tripId}
      openIn={canEdit ? 'immersiveMode' : 'newWindow'}
      isSoldOut={isSoldOut}
      sectionId={itemSectionId(item.id)}
      testId={testId}
      nonInteractable={nonInteractable}
      onClick={isTabletOrSmaller ? undefined : showOnMap}
    >
      <ItemImageContainer
        item={item}
        name={name}
        imgId={imgId}
        imgSrc={imgSrc}
        placeId={placeId}
        imageOverlayContent={
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <div>{imageOverlayContent}</div>
            {!nonInteractable && canEdit && (
              <>
                <CSSBreakpoint max="mobile">
                  {saveButtonAndItineraryButtonGroup}
                </CSSBreakpoint>
                <CSSBreakpoint only="desktop">
                  {saveButtonAndItineraryButtonGroup}
                </CSSBreakpoint>
              </>
            )}
          </Group>
        }
      />
      <Content>
        <CSSBreakpoint min="tablet">
          <SavedButtonContainer>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {isSoldOut && (
                <StatusLabel variant="cancelled">Sold Out</StatusLabel>
              )}
              {!nonInteractable && canEdit && saveButtonAndItineraryButtonGroup}
            </Group>
          </SavedButtonContainer>
        </CSSBreakpoint>

        <MainContent>
          <TopRowGroup
            direction="horizontal"
            gap={8}
            verticalAlign="center"
            horizontalAlign="space-between"
          >
            <BodyTextBlock variant="small" startIcon={<Icon size="XS" />}>
              {ItemMetadata[type].singularName}
            </BodyTextBlock>
            <NonDesktopLayoutGroup
              direction="horizontal"
              gap={8}
              verticalAlign="center"
            >
              {isSoldOut && (
                <StatusLabel variant="cancelled">Sold Out</StatusLabel>
              )}
              {!!rating && showUserReviewsRating(rating, numberOfReviews) && (
                <NumberRating
                  rating={rating}
                  hideLink
                  variant="xs"
                  onlyNumber
                />
              )}
            </NonDesktopLayoutGroup>
          </TopRowGroup>
          {!isSoldOut && urgencyLabels && <NoPrint>{urgencyLabels}</NoPrint>}
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={8}>
              <VerticalSpacer gap={4}>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  {locationDescription}
                </Group>
                <HeadingFullPrint variant="heading5" lineClamp={2}>
                  {name}
                </HeadingFullPrint>
                {propertyDescription && (
                  <BodyTextBlock variant="medium">
                    {propertyDescription}
                  </BodyTextBlock>
                )}
              </VerticalSpacer>
              {!!rating && showUserReviewsRating(rating, numberOfReviews) && (
                <DesktopLayoutNumberRating
                  rating={rating}
                  hideLink
                  variant="xs"
                />
              )}
              {amenitiesContent}
              {inclusions}
            </VerticalSpacer>
            {descriptorsString && (
              <DescriptorsWrapper>
                <DateContainer>
                  <BodyTextBlock
                    variant="medium"
                    weight="normal"
                    startIcon={<LineNotesIcon size="S" />}
                  >
                    {descriptorsString}
                  </BodyTextBlock>
                  {variant === 'tripSummary' && (
                    <OutsideTripDatesIcon
                      item={item}
                      changeItemDates={onClickAddTripItemDates}
                    />
                  )}
                </DateContainer>
              </DescriptorsWrapper>
            )}
            {printDescriptorsString && (
              <PrintOnly>
                <BodyText variant="medium" weight="semi-bold">
                  {printDescriptorsString}
                </BodyText>
              </PrintOnly>
            )}
            {attachedDocuments.map((document) => (
              <AttachedDocument key={document.filename} document={document} />
            ))}
            <Group
              direction="horizontal"
              wrap="wrap-reverse"
              gap="12 8"
              noDisplayWhenEmpty
            >
              {variant !== 'drawer' && <TripItemInlineNoteEdit item={item} />}
              {documentContext && canEdit && (
                <AssistChip
                  size="small"
                  onClick={(e) => {
                    documentContext.onRequestAddDocument(item.id)
                    e.stopPropagation()
                  }}
                  variant="filled"
                  startIcon={<LinePaperclipIcon />}
                >
                  Attach document
                </AssistChip>
              )}
            </Group>
          </VerticalSpacer>
          <VerticalSpacer gap={16}>
            {tripNameLabel}
            <OnlyIfChildren as={SavedBottomContent}>
              {priceAreaContent && (
                <div className="price-area">{priceAreaContent}</div>
              )}
              {ctaAreaContent && (
                <div className="cta-area">{ctaAreaContent}</div>
              )}
            </OnlyIfChildren>
          </VerticalSpacer>
        </MainContent>
      </Content>
    </BaseSavedOffer>
  )
}

export default memoWithNestedProps(SavedOfferCard, [
  'descriptors',
  'printDescriptors',
])
